/**
 * ТЗ 0084 — лиды Kommo в ERP: словари бэкенда и чистые помощники.
 *
 * Коды и виды приходят строками (бэкенд не делает из них graphene-enum):
 * сравнивать только с константами отсюда. Каждый словарь — это и список
 * ключей `leads.*`, который проверяет локаль-гейт
 * (`locales/__tests__/leads.locale.test.ts`): код без перевода уронит тест, а
 * не покажет сырой ключ. Незнакомый код экран показывает как есть.
 *
 * Модуль без React, Apollo и i18n — всё здесь проверяется юнит-тестами.
 */

import { parseJsonValue } from 'core/utils/sendgridTemplates';

import { CANDIDATE_STATUSES } from '../Dofinansowanie/dofinansowanieModel';

/** Строк на странице списка (предел сервера — 100). */
export const LEAD_PAGE_SIZE = 50;

/** Карточка лида — в адресе: `?lead=<id>`. */
export const LEAD_CARD_PARAM = 'lead';

/**
 * Перепроверка 0084: карточка открыта сразу с формой — `?compose=sms`
 * (кнопка «SMS» в строке списка): вкладка «Переписка», форма SMS открыта,
 * курсор в тексте.
 */
export const LEAD_COMPOSE_PARAM = 'compose';
export const LEAD_COMPOSE_KINDS = ['sms'] as const;
export type LeadCompose = typeof LEAD_COMPOSE_KINDS[number];

/** Вкладка раздела — в адресе: `?tab=kommo` (только Admin). */
export const LEADS_TAB_PARAM = 'tab';
export const LEADS_TABS = ['list', 'kommo'] as const;
export type LeadsTab = typeof LEADS_TABS[number];

/** Фильтр «Ответственный»: я, никто (или не сопоставлен с ERP), сотрудник. */
export const LEAD_RESPONSIBLE_ME = 'me';
export const LEAD_RESPONSIBLE_NONE = 'none';

/**
 * Вкладки карточки лида. Этап 3: «Переписка» (письма и SMS одной лентой) —
 * первая и по умолчанию; отдельной вкладки SMS больше нет, как у договора
 * (ТЗ 0083).
 */
export const LEAD_CARD_TABS = ['mail', 'notes', 'journal'] as const;
export type LeadCardTab = typeof LEAD_CARD_TABS[number];

/** Откуда лиды (`KommoLeadPageType.source`). */
export const KOMMO_LEAD_SOURCES = ['kommo', 'fake', 'disabled'] as const;

/**
 * Вид примечания Kommo. Незнакомый (у Kommo их больше десятка) — «Другое»:
 * пустая подпись хуже общей.
 */
export const KOMMO_NOTE_TYPES = [
  'common',
  'call_in',
  'call_out',
  'sms_in',
  'sms_out',
  'service_message',
  'other',
] as const;
export type KommoNoteType = typeof KOMMO_NOTE_TYPES[number];

/**
 * Наше примечание, которого в Kommo ещё нет (`KommoNoteType.erpStatus`):
 * ждёт очереди, пишется, Kommo не принял, запись выключена на сервере,
 * вымышленный источник (локальный стенд).
 */
export const KOMMO_NOTE_ERP_STATUSES = [
  'pending',
  'sending',
  'failed',
  'skipped',
  'fake',
] as const;

/** Журнал карточки лида (`LeadEventType.kind`). */
export const LEAD_EVENT_KINDS = [
  'sms_sent',
  'sms_failed',
  'note_failed',
  // Этапы 2–3: лесенки, письма, приглашение к анкете.
  'ladder_started',
  'ladder_stopped',
  'ladder_resumed',
  'ladder_finished',
  'email_sent',
  'invited',
] as const;

/**
 * Коды отказов: Kommo недоступен (`CrmErrorType.code` у примечаний),
 * мутации раздела (`customErrors.code`) и сеть/права. Перевод —
 * `leads.errors.<CODE>`; незнакомый — текст сервера.
 */
export const LEAD_ERROR_CODES = [
  'NOT_CONFIGURED',
  'DISABLED',
  'NETWORK',
  'TIMEOUT',
  'AUTH',
  'RATE_LIMITED',
  'NO_PIPELINE',
  'ERROR',
  'LEAD_NOT_FOUND',
  'NOT_FOUND',
  'QUEUE_UNAVAILABLE',
  'NO_ACCESS',
  // Этап 2: лесенки.
  'LEAD_CLOSED',
  'LEAD_DELETED',
  'NOTHING_TO_STOP',
  'NOT_STOPPED',
  'INVALID_KIND',
  'BUSY',
  // Этап 3: письма и приглашение к анкете.
  'NOT_YOUR_MAILBOX',
  'NO_EMAIL',
  'REQUIRED',
  'UNKNOWN_PLACEHOLDER',
  'LINK_IN_SUBJECT',
  'MAIL_ERROR',
  'IN_PROGRESS',
  'NO_FORM_URL',
  // Перепроверка 0084: ответы треда письма (ответ в тред, ящик) и длина.
  'DOES_NOT_EXIST',
  'MAILBOX_DISABLED',
  'GMAIL_ERROR',
  'TOO_LONG',
  'MAIL_DISABLED',
  'NO_MAILBOX',
  // ТЗ 0084: ссылки в письме и SMS — только короткие (сократителя нет —
  // отказ); `{link}` в теме без ссылки; нет шаблона текста.
  'SHORTLINK_DISABLED',
  'NO_LINK_HERE',
  'NO_TEMPLATE',
  // ТЗ 0086: ручная связь лида — неизвестный `what`.
  'INVALID',
] as const;

/**
 * Отказы по существу ДО письма: сервер письмо не отправлял и ключ запроса не
 * занимал. После такого отказа форма берёт новый ключ — исправленное письмо
 * уйдёт отдельной попыткой. Всё прочее (сеть, падение, `IN_PROGRESS`, ошибка
 * почты) — ключ ПРЕЖНИЙ: письмо могло уйти, и повтор с тем же ключом вернёт
 * его, а не отправит второе.
 */
export const LEAD_MAIL_REFUSALS = [
  'REQUIRED',
  'NO_EMAIL',
  'NO_MAILBOX',
  'NOT_YOUR_MAILBOX',
  'MAILBOX_DISABLED',
  'UNKNOWN_PLACEHOLDER',
  'LINK_IN_SUBJECT',
  'LEAD_CLOSED',
  'LEAD_DELETED',
  'LEAD_NOT_FOUND',
  'MAIL_DISABLED',
  'NO_FORM_URL',
  'TOO_LONG',
  'INVALID_KIND',
] as const;

const isMailRefusal = (code: string): boolean =>
  (LEAD_MAIL_REFUSALS as readonly string[]).includes(code) ||
  code.startsWith('INVALID');

/**
 * Нужен ли форме письма новый ключ после ответа: только если ВСЕ ошибки —
 * отказы по существу до отправки. Пусто (падение, сеть) — ключ прежний.
 */
export const leadMailKeyRenews = (
  errors: ReadonlyArray<{ code?: string | null }>,
): boolean =>
  errors.length > 0 &&
  errors.every((error) => isMailRefusal((error.code || '').toUpperCase()));

/**
 * Откуда взялся сотрудник ERP за пользователем Kommo: совпал email, совпала
 * рабочая почта, Admin выбрал руками, несколько кандидатов, никого (`''`).
 */
export const KOMMO_MATCH_SOURCES = [
  'email',
  'mailbox',
  'manual',
  'ambiguous',
  'none',
] as const;
export type KommoMatchSource = typeof KOMMO_MATCH_SOURCES[number];

/** Прогон синхронизации (`KommoSyncRunType.status`). */
export const KOMMO_SYNC_STATUSES = [
  'running',
  'success',
  'partial',
  'failed',
] as const;

/** Кто запустил прогон: расписание, кнопка в ERP, команда на сервере. */
export const KOMMO_SYNC_TRIGGERS = ['beat', 'erp', 'cli'] as const;

/** Опрос состояния синхронизации, пока прогон идёт. */
export const KOMMO_SYNC_POLL_MS = 4000;

/** Сколько ждать прогона после «Синхронизировать», прежде чем бросить опрос. */
export const KOMMO_SYNC_WATCH_MS = 3 * 60 * 1000;

/* ── Лесенки (этап 2) ───────────────────────────────────────────────── */

/** Вид лесенки (`LeadLadderRunType.kind`): напоминания или «Анкета». */
export const LEAD_LADDER_KINDS = ['reminder', 'dofin_form'] as const;
export type LeadLadderKind = typeof LEAD_LADDER_KINDS[number];

/**
 * Состояние запуска: идёт, остановлен руками, срок вышел (шагов больше нет,
 * ссылка на анкету живёт), закрыт по факту — анкета пришла, лид закрыт,
 * ушёл из воронки, удалён, заменён новым запуском.
 */
export const LEAD_LADDER_STATUSES = [
  'active',
  'stopped',
  'ended',
  'form_submitted',
  'lead_closed',
  'left_pipeline',
  'lead_deleted',
  'replaced',
] as const;

/** Фильтр списка «Лесенка» (`kommoLeads(ladder:)`). */
export const LEAD_LADDER_FILTERS = [
  'active',
  'reminder',
  'dofin_form',
  'none',
] as const;

/**
 * Почему активная лесенка сейчас не шлёт (`warning`, `ladderWarning`): план
 * выключен, робот выключен, и причины канала/номера SMS.
 */
export const LEAD_LADDER_WARNINGS = [
  'plan_disabled',
  'robot_off',
  'no_phone',
  'invalid_phone',
  'foreign_number',
  'disabled_on_server',
  'disabled',
  'entity_not_allowed',
  'not_allowlisted',
  'opted_out',
  'closed',
  // Приглашение: письмо ушло, а лесенку запустить не удалось (коллега
  // запустил её в ту же секунду) — запускают из карточки.
  'ladder_not_started',
  // Перепроверка 0084: у следующего шага нет включённого шаблона текста.
  'no_template',
] as const;

/**
 * Перепроверка 0084: почему SMS лиду сейчас не уходят — словами продавца,
 * ОДНИМ текстом по коду везде в карточке лида (шапка формы SMS, лента,
 * лесенки, приглашение). `leads.smsReasons.<code>`; у договоров и студентов
 * свои слова (`sms.skipReasons`: там «closed» — «оплачено, подписано»).
 */
export const LEAD_SMS_REASONS = [
  'closed',
  'no_phone',
  'invalid_phone',
  'foreign_number',
  'not_allowlisted',
  'opted_out',
  'disabled_on_server',
  'disabled',
  'entity_not_allowed',
  'plan_disabled',
  'robot_off',
  'daily_cap',
  'stale',
  'stopped_manually',
  'cancelled',
  'cancelled_by_stop',
  'superseded',
  // ТЗ 0084: текст шага — только шаблон ERP; сократитель не настроен.
  'no_template',
  'shortlink_disabled',
] as const;

/** Ключ причины (`leads.smsReasons.<code>`) или `null` — незнакомая. */
export const leadSmsReasonKey = (reason: string | null | undefined) => {
  const code = (reason || '').trim().toLowerCase();
  return code && (LEAD_SMS_REASONS as readonly string[]).includes(code)
    ? `leads.smsReasons.${code}`
    : null;
};

/** Дней «Анкеты» (по одному SMS в день): 1..7, по умолчанию 3 — как сервер. */
export const LEAD_LADDER_DAYS_MIN = 1;
export const LEAD_LADDER_DAYS_MAX = 7;
export const LEAD_LADDER_DAYS_DEFAULT = 3;

type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info' | 'gold';

const isOneOf = <T extends string>(
  values: readonly T[],
  value: string | null | undefined,
): value is T =>
  value !== null &&
  value !== undefined &&
  (values as readonly string[]).includes(value);

const familyKey = (
  family: string,
  values: readonly string[],
  value: string | null | undefined,
): string | null =>
  value && values.includes(value) ? `leads.${family}.${value}` : null;

/* ── Фильтры в адресе ───────────────────────────────────────────────── */

export const LEADS_FILTER_PARAMS = {
  stages: 'stage',
  responsible: 'resp',
  search: 'q',
  includeClosed: 'closed',
  ladder: 'ladder',
  page: 'page',
  // ТЗ 0086: метки (повторяющийся параметр, пустое значение — «без
  // метки»), связь с дофинансированием и студентом, сортировка.
  utmSource: 'src',
  utmMedium: 'med',
  utmCampaign: 'camp',
  funding: 'fund',
  student: 'stud',
  sort: 'sort',
} as const;

/**
 * «(без метки)» в выпадашке. Сервер понимает пустую строку в списке значений
 * (`utmSource: ['']`), а у опции antd пустое значение неотличимо от «ничего
 * не выбрано» — поэтому в интерфейсе маркер, а на сервер и в адрес уходит
 * `''` (`utmFromChoice`).
 */
export const LEAD_UTM_NONE = '__none__';

/** Сколько значений одной метки держит адрес: больше — мусор или атака. */
const UTM_VALUES_MAX = 30;
/** Длина значения метки в адресе — как колонка на сервере. */
const UTM_VALUE_MAX = 255;

/** Сортировка списка (`kommoLeads(sort:)`); `updated` — по умолчанию. */
export const LEAD_SORTS = [
  'updated',
  'created',
  'created_asc',
  'utm_source',
  'utm_campaign',
] as const;
export type LeadSort = typeof LEAD_SORTS[number];

/** Фильтр «Дофинансирование»: есть связь / нет — или этап воронки. */
export const LEAD_FUNDING_YES = 'yes';
export const LEAD_FUNDING_NO = 'no';

/** Фильтр «Студент»: стал / не стал. */
export const LEAD_STUDENT_FILTERS = ['yes', 'no'] as const;

export interface ILeadsFilters {
  /** id этапов Kommo; пусто — все. */
  stages: string[];
  /** `me` | `none` | id сотрудника ERP | пусто — все. */
  responsible: string;
  /** Имя, телефон, email, id лида в Kommo. */
  search: string;
  /** Закрытые лиды (этапы 142/143), удалённые и ушедшие из воронки. */
  includeClosed: boolean;
  /**
   * Этап 2 — активная лесенка: `active` (любая), `reminder`, `dofin_form`,
   * `none` (ни одной активной); пусто — все.
   */
  ladder: string;
  /** Страница с нуля; в адресе — с единицы. */
  page: number;
  /** ТЗ 0086: значения меток; `''` — «без метки». Пусто — все. */
  utmSource: string[];
  utmMedium: string[];
  utmCampaign: string[];
  /** `yes` | `no` | этап воронки дофинансирования | пусто — все. */
  funding: string;
  /** `yes` (стал студентом) | `no` | пусто — все. */
  student: string;
  /** Одна из `LEAD_SORTS`; пусто — `updated`. */
  sort: string;
}

export const EMPTY_LEADS_FILTERS: ILeadsFilters = {
  stages: [],
  responsible: '',
  search: '',
  includeClosed: false,
  ladder: '',
  page: 0,
  utmSource: [],
  utmMedium: [],
  utmCampaign: [],
  funding: '',
  student: '',
  sort: '',
};

/** Поиск на сервере режется до 100 знаков — так же и здесь. */
const SEARCH_MAX = 100;

/** Идентификатор из адреса: цифры или base64 id Relay. */
const cleanId = (value: string | null): string => {
  const id = (value || '').trim();
  return /^[A-Za-z0-9+/=_-]{1,120}$/.test(id) ? id : '';
};

/** id этапа Kommo — число; мусор из адреса на сервер не уходит. */
const cleanStages = (value: string | null): string[] =>
  Array.from(
    new Set(
      (value || '')
        .split(',')
        .map((part) => part.trim())
        .filter((part) => /^\d{1,12}$/.test(part)),
    ),
  );

const cleanResponsible = (value: string | null): string => {
  const raw = (value || '').trim();
  if (raw === LEAD_RESPONSIBLE_ME || raw === LEAD_RESPONSIBLE_NONE) return raw;
  return cleanId(raw);
};

/** Номер страницы из адреса (с единицы) → с нуля; мусор — первая. */
const cleanPage = (value: string | null): number => {
  const raw = (value || '').trim();
  if (!/^\d{1,5}$/.test(raw)) return 0;
  return Math.max(0, Number(raw) - 1);
};

const ladderParam = (value: string | null): string =>
  isOneOf(LEAD_LADDER_FILTERS, value) ? value : '';

/**
 * Значения метки: без повторов, обрезаны, не больше 30. Пустая строка —
 * законное значение («без метки»), её не выбрасываем.
 */
const cleanUtmValues = (values: ReadonlyArray<string | null>): string[] =>
  Array.from(
    new Set(
      values
        .filter((value): value is string => value !== null)
        .map((value) => value.trim().slice(0, UTM_VALUE_MAX)),
    ),
  ).slice(0, UTM_VALUES_MAX);

/** `yes` / `no` / этап воронки дофинансирования; мусор — «все». */
const fundingParam = (value: string | null): string => {
  const raw = (value || '').trim();
  if (raw === LEAD_FUNDING_YES || raw === LEAD_FUNDING_NO) return raw;
  return isOneOf(CANDIDATE_STATUSES, raw) ? raw : '';
};

const studentParam = (value: string | null): string =>
  isOneOf(LEAD_STUDENT_FILTERS, value) ? value : '';

/** Сортировка из адреса; по умолчанию (`updated`) — пусто. */
const sortParam = (value: string | null): string =>
  isOneOf(LEAD_SORTS, value) && value !== 'updated' ? value : '';

export const parseLeadsFilters = (search: string): ILeadsFilters => {
  const params = new URLSearchParams(search);
  const get = (key: keyof typeof LEADS_FILTER_PARAMS) =>
    params.get(LEADS_FILTER_PARAMS[key]);
  const all = (key: keyof typeof LEADS_FILTER_PARAMS) =>
    cleanUtmValues(params.getAll(LEADS_FILTER_PARAMS[key]));
  return {
    stages: cleanStages(get('stages')),
    responsible: cleanResponsible(get('responsible')),
    search: (get('search') || '').trim().slice(0, SEARCH_MAX),
    includeClosed: get('includeClosed') === '1',
    ladder: ladderParam(get('ladder')),
    page: cleanPage(get('page')),
    utmSource: all('utmSource'),
    utmMedium: all('utmMedium'),
    utmCampaign: all('utmCampaign'),
    funding: fundingParam(get('funding')),
    student: studentParam(get('student')),
    sort: sortParam(get('sort')),
  };
};

/**
 * Новая строка адреса: `patch` поверх фильтров. Прочие параметры (`?lead=`,
 * `?tab=`) не трогаются, пустое значение удаляет параметр.
 *
 * Смена любого фильтра, кроме самой страницы, возвращает на первую
 * страницу: «страница 4» другого набора лидов — чужая страница.
 */
export const serializeLeadsFilters = (
  search: string,
  patch: Partial<ILeadsFilters>,
): string => {
  const params = new URLSearchParams(search);
  const current = parseLeadsFilters(search);
  const next: ILeadsFilters = { ...current, ...patch };
  const filterChanged = (Object.keys(patch) as Array<keyof ILeadsFilters>).some(
    (key) => key !== 'page',
  );
  if (filterChanged && patch.page === undefined) next.page = 0;
  const set = (key: keyof typeof LEADS_FILTER_PARAMS, value: string) => {
    if (value) params.set(LEADS_FILTER_PARAMS[key], value);
    else params.delete(LEADS_FILTER_PARAMS[key]);
  };
  set('stages', cleanStages(next.stages.join(',')).join(','));
  set('responsible', cleanResponsible(next.responsible));
  set('search', next.search.trim().slice(0, SEARCH_MAX));
  set('includeClosed', next.includeClosed ? '1' : '');
  set('ladder', ladderParam(next.ladder));
  set('page', next.page > 0 ? String(Math.floor(next.page) + 1) : '');
  // Метки — повторяющимся параметром: `src=google&src=` — «google» и «без
  // метки». Запятая внутри значения метки (кампании её пишут) не ломает
  // разбор, как сломала бы склейка через запятую.
  (['utmSource', 'utmMedium', 'utmCampaign'] as const).forEach((key) => {
    params.delete(LEADS_FILTER_PARAMS[key]);
    cleanUtmValues(next[key]).forEach((value) =>
      params.append(LEADS_FILTER_PARAMS[key], value),
    );
  });
  set('funding', fundingParam(next.funding));
  set('student', studentParam(next.student));
  set('sort', sortParam(next.sort));
  return params.toString();
};

/** Сужен ли список (страница и сортировка — не фильтр). */
export const hasLeadsFilters = (filters: ILeadsFilters): boolean =>
  filters.stages.length > 0 ||
  Boolean(filters.responsible) ||
  Boolean(filters.search) ||
  filters.includeClosed ||
  Boolean(filters.ladder) ||
  filters.utmSource.length > 0 ||
  filters.utmMedium.length > 0 ||
  filters.utmCampaign.length > 0 ||
  Boolean(filters.funding) ||
  Boolean(filters.student);

/** Есть что сбрасывать: фильтр или сортировка не по умолчанию. */
export const canResetLeadsFilters = (filters: ILeadsFilters): boolean =>
  hasLeadsFilters(filters) || Boolean(filters.sort);

const yesNo = (value: string): boolean | null => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
};

/** Переменные запроса списка: пустое — `null` (одна запись кэша). */
export const toLeadsQueryVariables = (
  filters: ILeadsFilters,
  pageSize: number = LEAD_PAGE_SIZE,
) => {
  const fundingStatus =
    filters.funding &&
    filters.funding !== LEAD_FUNDING_YES &&
    filters.funding !== LEAD_FUNDING_NO
      ? [filters.funding]
      : null;
  return {
    statusIds: filters.stages.length ? filters.stages : null,
    responsible: filters.responsible || null,
    search: filters.search || null,
    includeClosed: filters.includeClosed ? true : null,
    ladder: filters.ladder || null,
    utmSource: filters.utmSource.length ? filters.utmSource : null,
    utmMedium: filters.utmMedium.length ? filters.utmMedium : null,
    utmCampaign: filters.utmCampaign.length ? filters.utmCampaign : null,
    fundingStatus,
    hasFunding: fundingStatus ? null : yesNo(filters.funding),
    hasStudent: yesNo(filters.student),
    sort: filters.sort || null,
    offset: Math.max(0, filters.page) * pageSize,
    limit: pageSize,
  };
};

/* ── ТЗ 0086: метки лида ───────────────────────────────────────────── */

/** Выбор выпадашки → значения для сервера и адреса: маркер → `''`. */
export const utmFromChoice = (choice: ReadonlyArray<string>): string[] =>
  cleanUtmValues(choice.map((value) => (value === LEAD_UTM_NONE ? '' : value)));

/** Значения из адреса → выбор выпадашки: `''` → маркер. */
export const utmToChoice = (values: ReadonlyArray<string>): string[] =>
  values.map((value) => (value === '' ? LEAD_UTM_NONE : value));

export interface ILeadFacetValue {
  value: string;
  count: number;
}

export interface ILeadFacetOption {
  /** Значение для выпадашки: `LEAD_UTM_NONE` вместо `''`. */
  choice: string;
  /** Значение метки; `''` — «без метки». */
  value: string;
  /** Лидов с этим значением во всём зеркале; `null` — не знаем (из адреса). */
  count: number | null;
}

/**
 * Варианты фильтра метки: значения со счётчиками по ВСЕМУ зеркалу (сервер
 * считает не по отфильтрованной ленте — выбор не схлопывает список) плюс
 * выбранные, которых среди них нет (ссылка коллеги). «Без метки» — первым:
 * его ищут чаще всего, и в хвосте из двухсот кампаний он бы потерялся.
 */
export const leadFacetOptions = (
  facet: ReadonlyArray<ILeadFacetValue> | null | undefined,
  selected: ReadonlyArray<string>,
): ILeadFacetOption[] => {
  const seen = new Set<string>();
  const out: ILeadFacetOption[] = [];
  (facet ?? []).forEach(({ value, count }) => {
    if (seen.has(value)) return;
    seen.add(value);
    out.push({
      choice: value === '' ? LEAD_UTM_NONE : value,
      value,
      count,
    });
  });
  selected.forEach((value) => {
    if (seen.has(value)) return;
    seen.add(value);
    out.push({
      choice: value === '' ? LEAD_UTM_NONE : value,
      value,
      count: null,
    });
  });
  const none = out.filter((option) => option.value === '');
  return [...none, ...out.filter((option) => option.value !== '')];
};

export interface ILeadUtm {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  content?: string | null;
  term?: string | null;
  referrer?: string | null;
  formName?: string | null;
  tracking?: unknown;
}

/**
 * Колонка «Источник»: `источник / канал` первой строкой, кампания — второй.
 * Меток нет вовсе — пустые строки (экран пишет «—»).
 */
export const leadUtmSummary = (
  utm: ILeadUtm | null | undefined,
): { line: string; campaign: string } => ({
  line: [utm?.source, utm?.medium]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' / '),
  campaign: (utm?.campaign || '').trim(),
});

/** Метки карточки «Откуда пришёл» — по порядку, пустые пропускаются. */
export const LEAD_UTM_KEYS = [
  'source',
  'medium',
  'campaign',
  'content',
  'term',
  'referrer',
  'formName',
] as const;
export type LeadUtmField = typeof LEAD_UTM_KEYS[number];

export const leadUtmRows = (
  utm: ILeadUtm | null | undefined,
): Array<{ field: LeadUtmField; value: string }> =>
  LEAD_UTM_KEYS.map((field) => ({
    field,
    value: String(utm?.[field] ?? '').trim(),
  })).filter((row) => row.value);

/**
 * Все метки лида как есть (`tracking`, graphene `JSONString`): код поля
 * Kommo → значение. Не объект — пусто.
 */
export const leadTrackingEntries = (
  tracking: unknown,
): Array<{ key: string; value: string }> => {
  const data = parseJsonValue(tracking);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  return Object.entries(data as Record<string, unknown>)
    .map(([key, value]) => ({
      key,
      value:
        value === null || value === undefined
          ? ''
          : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value),
    }))
    .filter((row) => row.value.trim())
    .sort((a, b) => a.key.localeCompare(b.key));
};

/* ── ТЗ 0086: связи лида с дофинансированием и студентом ───────────── */

/** Что связывают: заявку дофинансирования или студента. */
export const LEAD_LINK_TARGETS = ['candidate', 'student'] as const;
export type LeadLinkTarget = typeof LEAD_LINK_TARGETS[number];

/**
 * По какому правилу связь поставлена (`candidateRule`, `userRule`,
 * `PersonKommoLeadType.linkRule`): анкета по ссылке лида, договор из лида,
 * аккаунт связанной заявки, email, телефон, руками; `ambiguous` — несколько
 * разных людей, связи нет.
 */
export const LEAD_LINK_RULES = [
  'form',
  'contract',
  'candidate',
  'email',
  'phone',
  'manual',
  'ambiguous',
] as const;
export type LeadLinkRule = typeof LEAD_LINK_RULES[number];

/** Подпись правила (`leads.links.rules.<rule>`) или `null` — не связано. */
export const leadLinkRuleKey = (rule: string | null | undefined) =>
  familyKey('links.rules', LEAD_LINK_RULES, rule);

/**
 * Тон правила: сильное доказательство (анкета по ссылке, договор) — «ок»,
 * совпадение контакта — «инфо», руками — «золото», неоднозначно — «внимание».
 */
export const leadLinkRuleTone = (rule: string | null | undefined): Tone => {
  switch (rule) {
    case 'form':
    case 'contract':
      return 'ok';
    case 'candidate':
    case 'email':
    case 'phone':
      return 'info';
    case 'manual':
      return 'gold';
    case 'ambiguous':
      return 'warn';
    default:
      return 'neutral';
  }
};

/** Состояние блока связи в карточке лида. */
export type LeadLinkState = 'linked' | 'ambiguous' | 'none';

/**
 * Связано — показываем связь; `ambiguous` — «несколько совпадений,
 * выберите»; иначе «не найдено». Правило `ambiguous` при живой связи не
 * бывает (сервер ставит его только без связи), но если придёт — важнее
 * связь.
 */
export const leadLinkState = (
  rule: string | null | undefined,
  linked: boolean,
): LeadLinkState => {
  if (linked) return 'linked';
  return rule === 'ambiguous' ? 'ambiguous' : 'none';
};

/** Чип этапа: был выбран — снять, не был — добавить. */
export const toggleLeadStage = (
  stages: readonly string[],
  id: string,
): string[] =>
  stages.includes(id)
    ? stages.filter((value) => value !== id)
    : [...stages, id];

/** id открытой карточки из адреса или `null`. */
export const parseLeadCard = (search: string): string | null =>
  cleanId(new URLSearchParams(search).get(LEAD_CARD_PARAM)) || null;

/**
 * Адрес с открытой (`id`) или закрытой (`null`) карточкой. `compose` —
 * открыть сразу с формой (`?compose=sms`); закрытие убирает и её.
 */
export const withLeadCard = (
  search: string,
  id: string | null,
  compose: LeadCompose | null = null,
): string => {
  const params = new URLSearchParams(search);
  if (id) params.set(LEAD_CARD_PARAM, id);
  else params.delete(LEAD_CARD_PARAM);
  if (id && compose) params.set(LEAD_COMPOSE_PARAM, compose);
  else params.delete(LEAD_COMPOSE_PARAM);
  return params.toString();
};

/** С какой формой открыть карточку (`?compose=sms`) или `null`. */
export const parseLeadCompose = (search: string): LeadCompose | null => {
  const value = new URLSearchParams(search).get(LEAD_COMPOSE_PARAM);
  return isOneOf(LEAD_COMPOSE_KINDS, value) ? value : null;
};

/** Вкладка раздела: «Kommo» — только Admin; прочим — список. */
export const parseLeadsTab = (search: string, isAdmin: boolean): LeadsTab => {
  const value = new URLSearchParams(search).get(LEADS_TAB_PARAM);
  return isAdmin && value === 'kommo' ? 'kommo' : 'list';
};

/** Адрес с вкладкой; список (по умолчанию) параметр удаляет. */
export const withLeadsTab = (search: string, tab: LeadsTab): string => {
  const params = new URLSearchParams(search);
  if (tab === 'list') params.delete(LEADS_TAB_PARAM);
  else params.set(LEADS_TAB_PARAM, tab);
  return params.toString();
};

/* ── Ответственные для фильтра ──────────────────────────────────────── */

export interface IResponsibleOption {
  /** id сотрудника ERP. */
  id: string;
  name: string;
}

interface IResponsibleRow {
  responsibleUserId?: string | null;
  responsibleUserName?: string | null;
}

/**
 * Варианты фильтра «Ответственный» — все, кого экран уже видел в списке, а
 * не только на текущей странице: после выбора человека список схлопнулся бы
 * до него одного, и переключиться на соседа было бы не из чего (грабли
 * `filter-options-from-filtered-feed`).
 *
 * Ничего нового — ТОТ ЖЕ массив: вызов стоит в эффекте на каждый ответ
 * списка, и новый массив без повода перерисовывал бы фильтры по кругу.
 */
export const mergeResponsibleOptions = (
  current: ReadonlyArray<IResponsibleOption>,
  rows: ReadonlyArray<IResponsibleRow>,
): ReadonlyArray<IResponsibleOption> => {
  const known = new Map(current.map((option) => [option.id, option.name]));
  let changed = false;
  rows.forEach((row) => {
    const id = row.responsibleUserId ? String(row.responsibleUserId) : '';
    const name = (row.responsibleUserName || '').trim();
    if (!id || !name) return;
    if (known.get(id) === name) return;
    known.set(id, name);
    changed = true;
  });
  if (!changed) return current;
  return Array.from(known.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/* ── Этап: цвет из Kommo ────────────────────────────────────────────── */

export interface IStageChipColors {
  background: string;
  border: string;
  color: string;
}

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const toRgb = (hex: string): [number, number, number] | null => {
  const match = HEX_RE.exec(hex.trim());
  if (!match) return null;
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : match[1];
  return [
    parseInt(digits.slice(0, 2), 16),
    parseInt(digits.slice(2, 4), 16),
    parseInt(digits.slice(4, 6), 16),
  ];
};

const toHex = (rgb: [number, number, number]): string =>
  `#${rgb
    .map((part) =>
      Math.max(0, Math.min(255, Math.round(part)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/** Относительная яркость по WCAG (0 — чёрный, 1 — белый). */
const luminance = ([r, g, b]: [number, number, number]): number => {
  const channel = (value: number) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * Цвета чипа этапа — из цвета этапа в Kommo, чтобы продавец узнавал этап
 * так же, как в воронке. Фон — сам цвет, рамка — он же темнее, текст —
 * тёмный на светлом и белый на тёмном (у Kommo пастельные, но руками
 * ставят любые). Не цвет (пусто, мусор) — `null`, чип нейтральный.
 */
export const stageChipColors = (
  hex: string | null | undefined,
): IStageChipColors | null => {
  const rgb = toRgb(hex || '');
  if (!rgb) return null;
  const light = luminance(rgb) > 0.45;
  return {
    background: toHex(rgb),
    border: toHex([rgb[0] * 0.78, rgb[1] * 0.78, rgb[2] * 0.78]),
    color: light ? '#1e293b' : '#ffffff',
  };
};

/* ── Примечания ─────────────────────────────────────────────────────── */

/** Вид примечания для подписи и значка; незнакомый — `other`. */
export const kommoNoteType = (
  value: string | null | undefined,
): KommoNoteType => (isOneOf(KOMMO_NOTE_TYPES, value) ? value : 'other');

export const kommoNoteTypeKey = (value: string | null | undefined): string =>
  `leads.notes.types.${kommoNoteType(value)}`;

export const isCallNote = (value: string | null | undefined): boolean =>
  value === 'call_in' || value === 'call_out';

export const kommoNoteErpStatusKey = (value: string | null | undefined) =>
  familyKey('notes.erpStatus', KOMMO_NOTE_ERP_STATUSES, value);

export const kommoNoteErpStatusTone = (
  value: string | null | undefined,
): Tone => {
  switch (value) {
    case 'pending':
    case 'sending':
      return 'warn';
    case 'failed':
      return 'danger';
    case 'fake':
      return 'info';
    default:
      return 'neutral';
  }
};

/** Длительность звонка: `95` → `1:35`, `3725` → `1:02:05`. */
export const formatCallDuration = (
  seconds: number | null | undefined,
): string => {
  if (seconds === null || seconds === undefined) return '';
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

/* ── Журнал, ошибки, сопоставление, синхронизация ──────────────────── */

export const leadEventKey = (kind: string | null | undefined) =>
  familyKey('events', LEAD_EVENT_KINDS, kind);

export const leadEventTone = (kind: string | null | undefined): Tone => {
  if (kind === 'sms_sent') return 'ok';
  if (kind === 'sms_failed' || kind === 'note_failed') return 'danger';
  return 'neutral';
};

export const leadErrorKey = (code: string | null | undefined) =>
  familyKey('errors', LEAD_ERROR_CODES, code);

/** Источник сопоставления; пусто — `none`, незнакомый — как есть. */
export const kommoMatchSource = (value: string | null | undefined): string =>
  (value || '').trim() || 'none';

export const kommoMatchKey = (value: string | null | undefined) =>
  familyKey('kommo.match', KOMMO_MATCH_SOURCES, kommoMatchSource(value));

export const kommoMatchTone = (value: string | null | undefined): Tone => {
  switch (kommoMatchSource(value)) {
    case 'email':
    case 'mailbox':
      return 'ok';
    case 'manual':
      return 'info';
    case 'ambiguous':
      return 'warn';
    default:
      return 'neutral';
  }
};

export const kommoSyncStatusKey = (value: string | null | undefined) =>
  familyKey('kommo.runStatus', KOMMO_SYNC_STATUSES, value);

export const kommoSyncStatusTone = (value: string | null | undefined): Tone => {
  switch (value) {
    case 'success':
      return 'ok';
    case 'partial':
      return 'warn';
    case 'failed':
      return 'danger';
    case 'running':
      return 'info';
    default:
      return 'neutral';
  }
};

export const kommoSyncTriggerKey = (value: string | null | undefined) =>
  familyKey('kommo.trigger', KOMMO_SYNC_TRIGGERS, value);

export const kommoLeadSourceKey = (value: string | null | undefined) =>
  familyKey('source', KOMMO_LEAD_SOURCES, value);

/** Кто лид на экране: человек, иначе название сделки, иначе id в Kommo. */
export const leadDisplayName = (lead: {
  personName?: string | null;
  name?: string | null;
  kommoId?: string | null;
}): string =>
  (lead.personName || '').trim() ||
  (lead.name || '').trim() ||
  (lead.kommoId ? `#${lead.kommoId}` : '—');

/* ── Лесенки: подписи, тоны, кнопки ─────────────────────────────────── */

export const leadLadderKindKey = (kind: string | null | undefined) =>
  familyKey('ladders.kinds', LEAD_LADDER_KINDS, kind);

export const leadLadderStatusKey = (status: string | null | undefined) =>
  familyKey('ladders.statuses', LEAD_LADDER_STATUSES, status);

export const leadLadderWarningKey = (warning: string | null | undefined) =>
  familyKey('ladders.warnings', LEAD_LADDER_WARNINGS, warning);

export const leadLadderFilterKey = (value: string) =>
  familyKey('filters.ladders', LEAD_LADDER_FILTERS, value);

/**
 * Тон состояния: идёт — «инфо» (или «внимание», если сейчас не шлёт), анкета
 * пришла — «ок» (цель достигнута), остановлена — «внимание», остальное
 * закрыто — нейтрально.
 */
export const leadLadderStatusTone = (
  status: string | null | undefined,
  warning?: string | null,
): Tone => {
  switch (status) {
    case 'active':
      return warning ? 'warn' : 'info';
    case 'form_submitted':
      return 'ok';
    case 'stopped':
      return 'warn';
    default:
      return 'neutral';
  }
};

/** Запуск ещё «жив»: не закрыт по факту (идёт, остановлен или срок вышел). */
export const isLeadLadderOpen = (status: string | null | undefined): boolean =>
  status === 'active' || status === 'stopped' || status === 'ended';

export interface ILeadLadderActions {
  /** «Остановить» — идёт. */
  stop: boolean;
  /** «Возобновить» — остановлена руками. */
  resume: boolean;
  /** «Запустить заново» — срок вышел: шагов больше нет. */
  restart: boolean;
}

/**
 * Какие кнопки у запуска — по правилам сервера: остановить — идущий,
 * возобновить — остановленный; у запуска с вышедшим сроком — «Запустить
 * заново» (новый запуск того же вида). Закрытый по факту (анкета пришла,
 * лид закрыт, удалён, заменён) кнопок не имеет: закрытому лиду сервер новый
 * запуск не даст, а после анкеты он не нужен.
 */
export const leadLadderActions = (
  status: string | null | undefined,
): ILeadLadderActions => ({
  stop: status === 'active',
  resume: status === 'stopped',
  restart: status === 'ended',
});

/** Дней «Анкеты» из поля ввода: целое 1..7; пусто или мусор — `null`. */
export const parseLadderDays = (raw: string): number | null => {
  const text = String(raw ?? '').trim();
  if (!/^\d{1,2}$/.test(text)) return null;
  const value = Number(text);
  if (value < LEAD_LADDER_DAYS_MIN || value > LEAD_LADDER_DAYS_MAX) return null;
  return value;
};

/**
 * Чип лесенки в строке списка (`ladders`: последний запуск каждого вида):
 * показываем открытые (идёт, остановлена, срок вышел) и «анкета получена» —
 * остальное (заменена, лид закрыт) в списке шум.
 */
export const leadLadderChips = <T extends { kind: string; status: string }>(
  ladders: ReadonlyArray<T>,
): T[] =>
  ladders.filter(
    (ladder) =>
      isLeadLadderOpen(ladder.status) || ladder.status === 'form_submitted',
  );

/**
 * Перепроверка 0084 — история запусков в карточке. На виду по каждому виду:
 * идущий (или остановленный) запуск и последний запуск — остальное (старые
 * закрытые и заменённые) сворачивается в «История запусков (N)»: три
 * запуска выталкивали «Переписку» за экран. Порядок — как пришёл (новые
 * сверху).
 */
export const splitLadderRuns = <
  T extends { id: string; kind: string; status: string }
>(
  runs: ReadonlyArray<T>,
): { current: T[]; history: T[] } => {
  const keep = new Set<string>();
  const latest = new Set<string>();
  runs.forEach((run) => {
    if (run.status === 'active' || run.status === 'stopped') keep.add(run.id);
    if (!latest.has(run.kind)) {
      latest.add(run.kind);
      keep.add(run.id);
    }
  });
  return {
    current: runs.filter((run) => keep.has(run.id)),
    history: runs.filter((run) => !keep.has(run.id)),
  };
};

/** Строка `KommoLeadType.ladderReadiness`: пошлёт ли лесенка вида и что. */
export interface ILadderReadinessRow {
  kind: string;
  warning?: string | null;
  firstSms?: string | null;
  firstSmsSegments?: number | null;
}

/**
 * Почему лесенка вида НЕ пошлёт, если запустить её сейчас
 * (`KommoLeadType.ladderReadiness`), или `null` — пошлёт (или ответа нет).
 */
export const ladderReadinessWarning = (
  readiness: ReadonlyArray<ILadderReadinessRow> | null | undefined,
  kind: string,
): string | null =>
  (readiness ?? []).find((row) => row.kind === kind)?.warning || null;

/**
 * Первое SMS лесенки вида этому лиду — КАК УЙДЁТ (сервер строит его тем же
 * путём, что отправка: шаблон ERP с кодом шага, подстановки лида, ссылка —
 * образец той же длины). `null` — у плана нет шага (или ответа нет).
 */
export const ladderFirstSms = (
  readiness: ReadonlyArray<ILadderReadinessRow> | null | undefined,
  kind: string,
): { text: string; segments: number } | null => {
  const row = (readiness ?? []).find((item) => item.kind === kind);
  return row?.firstSms
    ? { text: row.firstSms, segments: row.firstSmsSegments || 1 }
    : null;
};

/**
 * Тема SMS лесенки вида: её шаблоны — тексты шагов («SMS → Шаблоны»,
 * `TEMPLATE_TOPICS` бэкенда, перепроверка 0084).
 */
export const LEAD_LADDER_TOPIC: Record<LeadLadderKind, string> = {
  reminder: 'lead_reminder',
  dofin_form: 'lead_dofin',
};

/* ── «Пригласить к анкете» (этап 3) ─────────────────────────────────── */

/** Почему приглашение отправить нельзя совсем. */
export const LEAD_INVITE_BLOCKS = ['deleted', 'noEmail', 'noMailbox'] as const;
export type LeadInviteBlock = typeof LEAD_INVITE_BLOCKS[number];

/** Что стоит знать ДО отправки: лесенка не сработает, анкету уже заполнял… */
export const LEAD_INVITE_WARNINGS = [
  'closedNoLadder',
  'ladderWontSend',
  'alreadyFilled',
] as const;
export type LeadInviteWarning = typeof LEAD_INVITE_WARNINGS[number];

export interface ILeadInviteCheck {
  isDeleted: boolean;
  isClosed: boolean;
  inPipeline: boolean;
  email: string;
  /** Ящиков, с которых сотрудник может писать; `null` — ещё грузятся. */
  mailboxes: number | null;
  /** Кандидатов дофинансирования с тем же телефоном или email. */
  fundingCandidates: number;
  /**
   * Почему лесенка «Анкета» НЕ пошлёт, если запустить её сейчас
   * (`ladderReadiness` сервера: нет телефона, тестовый режим, план или робот
   * выключен…); `null` — пошлёт или ещё неизвестно.
   */
  ladderWarning: string | null;
  /** Галочка «Напоминать SMS…». */
  withLadder: boolean;
}

/** Одна причина «нельзя» — кнопка и подпись под ней считаются одной функцией. */
export const leadInviteBlock = (
  check: ILeadInviteCheck,
): LeadInviteBlock | null => {
  if (check.isDeleted) return 'deleted';
  if (!check.email.trim()) return 'noEmail';
  if (check.mailboxes === 0) return 'noMailbox';
  return null;
};

/**
 * Предупреждения перед отправкой. Письмо уйдёт всё равно — но человек
 * должен знать, что лесенка не сработает или что анкету уже заполняли.
 * Закрытый лид с галочкой сервер отвергнет (`LEAD_CLOSED`) — об этом
 * предупреждение, а галочку снимает сам человек. Почему лесенка не
 * пошлёт — правило сервера (`ladderReadiness`), а не своя догадка экрана.
 */
export const leadInviteWarnings = (
  check: ILeadInviteCheck,
): LeadInviteWarning[] => {
  const out: LeadInviteWarning[] = [];
  const closed = check.isClosed || !check.inPipeline;
  if (check.withLadder && closed) out.push('closedNoLadder');
  if (
    check.withLadder &&
    !closed &&
    check.ladderWarning &&
    check.ladderWarning !== 'closed'
  ) {
    out.push('ladderWontSend');
  }
  if (check.fundingCandidates > 0) out.push('alreadyFilled');
  return out;
};

/* ── Телефон на экране ──────────────────────────────────────────────── */

/** Коды стран, которые встречаются у лидов, — от длинных к коротким. */
const COUNTRY_CODES = [
  '380',
  '375',
  '370',
  '371',
  '372',
  '48',
  '49',
  '44',
  '1',
  '7',
];

/**
 * Телефон для глаза: `+48500100100` → `+48 500 100 100`. Польский номер —
 * тройками; другой с известным кодом страны — код и тройки; всё
 * непонятное — как есть. Копируется всегда исходная строка.
 */
export const formatPhoneDisplay = (raw: string | null | undefined): string => {
  const value = String(raw ?? '').trim();
  const digits = value.replace(/\D+/g, '');
  if (
    !value.startsWith('+') ||
    digits.length < 8 ||
    /[^\d\s+()-]/.test(value)
  ) {
    return value;
  }
  const code = COUNTRY_CODES.find((cc) => digits.startsWith(cc));
  if (!code) return value;
  const rest = digits.slice(code.length);
  if (rest.length < 6) return value;
  const groups: string[] = [];
  for (let at = 0; at < rest.length; at += 3) {
    groups.push(rest.slice(at, at + 3));
  }
  // Хвост из одной цифры приклеиваем к предыдущей тройке: «500 100 100 1»
  // читается хуже, чем «500 100 1001».
  if (groups.length > 1 && groups[groups.length - 1].length === 1) {
    const tail = groups.pop() as string;
    groups[groups.length - 1] += tail;
  }
  return `+${code} ${groups.join(' ')}`;
};
